import { useState } from 'react'

import {
  type ClinicId,
  getStaffRegistrationLabel,
  isVet,
  type Treatment,
  type WrittenPrescriptionInput,
  writtenPrescriptionInputSchema,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { FormField } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import { useDownloadDocument } from '@/frontend/features/documents/use-download-document'
import { usePreviewDocument } from '@/frontend/features/documents/use-preview-document'
import { useSaveWrittenPrescription } from '@/frontend/features/documents/use-save-written-prescription'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

type Prescription = Pick<Treatment, 'id' | 'staffId' | 'isControlledSubstance'>
type Authorization = { type: 'none' } | { type: 'wet' | 'electronic'; vetId: string | null }
type SubmitAction = 'preview' | 'download' | 'save'

export function WrittenPrescriptionForm({
  prescription,
  onCancel,
  onComplete,
}: {
  prescription: Prescription
  onCancel: () => void
  onComplete: () => void
}) {
  const { clearPreview, preview, previewDocument } = usePreviewDocument()
  const { downloadDocument } = useDownloadDocument()
  const savePrescriptionMutation = useSaveWrittenPrescription()
  const { data: currentUser } = useGetAuthenticatedStaff()
  const { data: staffList } = useListStaff()

  /** Draft clinic and sign-off selection, request error, and pending state for all document actions. */
  const [clinicId, setClinicId] = useState<ClinicId | null>(null)
  const [authorization, setAuthorization] = useState<Authorization>({ type: 'none' })
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { id: treatmentId, staffId: prescribingVetId } = prescription
  const isControlledSubstance = prescription.isControlledSubstance === true
  const isCurrentUserVet = !!currentUser && isVet(currentUser)
  const isElectronic = authorization.type === 'electronic'
  const selectedVet =
    authorization.type === 'none'
      ? undefined
      : staffList?.find((staff) => staff.id === authorization.vetId)

  /** Validation error for an unavailable vet, missing registration number, or missing e-signature. */
  let validationError: string | null = null
  if (authorization.type !== 'none') {
    if (authorization.vetId && staffList && (!selectedVet || !isVet(selectedVet))) {
      validationError = 'The selected authorising vet is not available.'
    } else if (selectedVet && !selectedVet.licenseNumber) {
      validationError = `${selectedVet.firstName} ${selectedVet.lastName} has no ${getStaffRegistrationLabel(selectedVet.regions)} on file.`
    } else if (isElectronic && selectedVet && !selectedVet.hasEsignature) {
      validationError = `${selectedVet.firstName} ${selectedVet.lastName} does not have an e-signature on file.`
    }
  }
  /** Electronic signing is limited to the treatment creator or authenticated vet; wet signing permits any vet. */
  const allowedSigner =
    !isElectronic ||
    authorization.vetId === currentUser?.id ||
    authorization.vetId === prescribingVetId
  /** Signed documents require vet credentials; electronic signing also requires a signature and an eligible drug. */
  const canAuthorise =
    authorization.type === 'none' ||
    (allowedSigner &&
      !!selectedVet &&
      isVet(selectedVet) &&
      !!selectedVet.licenseNumber &&
      (!isElectronic || (selectedVet.hasEsignature && !isControlledSubstance)))
  /** The API schema validates the draft's clinic and conditional authorisation details. */
  const parsed = writtenPrescriptionInputSchema.safeParse({ treatmentId, clinicId, authorization })
  const input: WrittenPrescriptionInput | null = parsed.success ? parsed.data : null
  const canSubmit = !!input && canAuthorise && !isSubmitting

  async function submit(action: SubmitAction) {
    if (isSubmitting) return
    if (!input || !canAuthorise) {
      setError('Select a clinic and complete the authorization details.')
      return
    }
    setError(null)
    setIsSubmitting(true)
    if (action === 'save') {
      try {
        await savePrescriptionMutation.mutateAsync(input)
        onComplete()
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to process prescription')
      }
    } else {
      const request = { documentType: 'written-prescription' as const, input }
      const result =
        action === 'download' ? await downloadDocument(request) : await previewDocument(request)
      if (!result.success) setError(result.error.message)
    }
    setIsSubmitting(false)
  }

  return (
    <div className={`max-w-full ${preview ? 'w-5xl' : 'w-lg'}`}>
      <DialogHeader className="mb-4 flex-row items-center justify-between">
        <DialogTitle>Generate Written Prescription</DialogTitle>
        {preview && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              clearPreview()
              setError(null)
            }}
          >
            Back
          </Button>
        )}
      </DialogHeader>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {preview ? (
        <div className="flex h-[75vh] min-h-0 flex-col gap-4">
          <iframe
            title="Written prescription preview"
            src={preview.url}
            className="min-h-0 w-full flex-1 rounded border"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <FormField
            label="Originating clinic"
            required
            disabled={isSubmitting}
            render={({ id, disabled }) => (
              <ClinicSearchWithDefault
                id={id}
                value={clinicId}
                onChange={setClinicId}
                disabled={disabled}
              />
            )}
          />
          <FormField
            label="Authorisation"
            required
            labelUsesId
            disabled={isSubmitting}
            render={({ id, disabled }) => (
              <RadioGroup<Authorization['type']>
                aria-labelledby={id}
                value={authorization.type}
                disabled={disabled}
                onValueChange={(type) => {
                  setError(null)
                  setAuthorization(
                    type === 'none'
                      ? { type }
                      : {
                          type,
                          vetId:
                            type === 'electronic'
                              ? prescribingVetId
                              : isCurrentUserVet
                                ? currentUser.id
                                : null,
                        }
                  )
                }}
              >
                {[
                  { label: 'Empty sign-off', value: 'none' },
                  { label: 'Wet signature', value: 'wet' },
                  { label: 'Electronic signature', value: 'electronic' },
                ].map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`${id}-${option.value}`}
                    className="flex items-center gap-2 font-normal"
                  >
                    <RadioGroupItem
                      id={`${id}-${option.value}`}
                      value={option.value}
                      disabled={option.value === 'electronic' && isControlledSubstance}
                    />
                    {option.label}
                  </label>
                ))}
              </RadioGroup>
            )}
          />
          {authorization.type === 'wet' && (
            <FormField
              label="Authorising vet"
              required
              disabled={isSubmitting}
              render={({ id, disabled }) => (
                <StaffSearch
                  id={id}
                  value={authorization.vetId}
                  onChange={(vet) => {
                    setError(null)
                    setAuthorization({ type: 'wet', vetId: vet?.id ?? null })
                  }}
                  filter={isVet}
                  placeholder="Select authorising vet..."
                  allowClear
                  disabled={disabled}
                />
              )}
            />
          )}
          {authorization.type === 'electronic' && (
            <FormField
              label="Authorising vet"
              required
              labelUsesId
              disabled={isSubmitting}
              render={({ id, disabled }) => (
                <RadioGroup
                  aria-labelledby={id}
                  value={authorization.vetId ?? undefined}
                  disabled={disabled}
                  onValueChange={(vetId: string) => {
                    setError(null)
                    setAuthorization({ type: 'electronic', vetId })
                  }}
                >
                  <label
                    htmlFor={`${id}-prescriber`}
                    className="flex items-center gap-2 font-normal"
                  >
                    <RadioGroupItem
                      id={`${id}-prescriber`}
                      value={prescribingVetId}
                      disabled={!prescribingVetId}
                    />
                    Use prescribing vet's signature
                  </label>
                  {isCurrentUserVet && currentUser.id !== prescribingVetId && (
                    <label htmlFor={`${id}-self`} className="flex items-center gap-2 font-normal">
                      <RadioGroupItem id={`${id}-self`} value={currentUser.id} />
                      Use your own signature
                    </label>
                  )}
                </RadioGroup>
              )}
            />
          )}
          {isElectronic && isControlledSubstance && (
            <Alert variant="destructive">
              <AlertDescription>
                This is a controlled drug - e-signing is not permitted.
              </AlertDescription>
            </Alert>
          )}
          {validationError && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            {!isElectronic && (
              <Button
                type="button"
                variant="outline"
                onClick={() => submit('download')}
                disabled={!canSubmit}
              >
                Download PDF
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => submit('preview')}
              disabled={!canSubmit}
            >
              Preview PDF
            </Button>
            {isElectronic && (
              <Button type="button" onClick={() => submit('save')} disabled={!canSubmit}>
                {isSubmitting ? 'Saving...' : 'Generate & save'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function WrittenPrescriptionDialog({
  prescription,
  open,
  onOpenChange,
  nested = false,
}: {
  prescription: Prescription
  open: boolean
  nested?: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nested={nested}
        showCloseButton={false}
        className="w-fit grid-cols-[minmax(0,1fr)] sm:max-w-[calc(100%-2rem)]"
      >
        <WrittenPrescriptionForm
          prescription={prescription}
          onCancel={() => onOpenChange(false)}
          onComplete={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}
